import { CommandBar, Link, Stack } from "@fluentui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { IStep, ITerm, TermTypeEnum } from "../../interfaces/ITerm.interface";
import { buildCommandBarItems } from "../../utils/buildCommandBarItems";

import { TermInfoContainer } from "./styles";

import { contentStyles } from "@/components/Contracts/ViewMenu/styles";
import { useGetProfile } from "@/core/libs/api/react-query";
import { TERMS_QUERY_TOKEN } from "@/core/libs/api/react-query/hook/use-get-terms";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { ContractBadgeColors } from "@/modules/Contracts/enums";
import { Badge } from "@/modules/Contracts/styles";

interface ITermsViewMenu {
  term: ITerm;
  currentStep: IStep;
  refetchTerm: () => Promise<void>;
  setIsLoading(loading: boolean): void;
  handleDisplayTermLogs: (isOpen: boolean) => void;
}

export const TermViewMenu = ({
  term,
  currentStep,
  refetchTerm,
  setIsLoading,
  handleDisplayTermLogs
}: ITermsViewMenu): JSX.Element => {
  const userProfile = useGetProfile();

  const permissions = userProfile.data?.data?.role.permissions;
  const { execute } = useApi();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  const translateTermType: Record<TermTypeEnum, string> = {
    [TermTypeEnum.ASSOCIATION]: "Ficha de Matrícula",
    [TermTypeEnum.CHANGE_OF_OWNERSHIP]: "Documento Complementar",
    [TermTypeEnum.ASSOCIATION_AND_PROXY]: "Procuração"
  };
  const processTerm = useCallback(
    async (bodyObj: object, showAlert = false) => {
      setIsLoading(true);
      setIsProcessing(true);

      const processStep = await execute({
        url: `terms/process`,
        bodyObj,
        method: "POST",
        showAlert
      });

      if (processStep.response.status === 200) {
        await refetchTerm();
        void queryClient.invalidateQueries({ queryKey: [TERMS_QUERY_TOKEN] });
      }

      setIsProcessing(false);
      setIsLoading(false);
    },
    [setIsLoading, execute, refetchTerm, queryClient]
  );

  const advanceStep = useCallback(async () => {
    const bodyObj = {
      termId: term?.id,
      interactionAction: term?.currentStep.interactionAction ?? undefined,
      account: {
        id: term?.account.id,
        accountType: term?.account?.accountType,
        fullName: term?.account?.fullName,
        maritalStatusId: term?.account?.maritalStatusId,
        occupation: term?.account?.occupation,
        birthDate: term?.account?.birthDate,
        documents: term?.account?.documents,
        contacts: term?.account?.contacts,
        legalRepresentatives: term?.account?.legalRepresentatives
      }
    };

    await processTerm(bodyObj, true);
  }, [
    term?.id,
    term?.currentStep.interactionAction,
    term?.account,
    processTerm
  ]);

  const cancelOrRejectTerm = useCallback(
    async (actionName: string) => {
      await processTerm({ termId: term?.id, interactionAction: actionName });
    },
    [term?.id, processTerm]
  );

  const downloadTerm = useCallback(
    async (data?: { signed?: boolean }) => {
      const res = await execute({
        url: `terms/${term?.id}/pdf`,
        params: data,
        showAlert: true,
        messages: {
          success: "PDF gerado com sucesso",
          error: "PDF não disponível"
        }
      });

      if (res.data) {
        window.open(res.data, "_blank");
      }
    },
    [term?.id, execute]
  );

  const commandBarItems = buildCommandBarItems({
    term,
    downloadTerm,
    permissions,
    advanceStep,
    cancelOrRejectTerm,
    handleDisplayTermLogs,
    isProcessing
  });

  return (
    <Stack
      className={contentStyles.stackContainer}
      tokens={{ childrenGap: 10 }}
      horizontal
      verticalAlign="center"
      horizontalAlign="space-between"
    >
      <TermInfoContainer>
        <Stack>
          <h2 className="ms-fontSize-24" style={{ margin: 0 }}>
            {translateTermType[term.termType]} #{term?.id}
          </h2>
          {term.contractId && (
            <Link href={`/contracts/${term.contractId}`}>
              Anexado ao Contrato: #{term.contractId}
            </Link>
          )}
        </Stack>
        <Badge bgColor={ContractBadgeColors[currentStep?.internalName]}>
          {currentStep?.friendlyName}
        </Badge>
      </TermInfoContainer>
      <CommandBar items={commandBarItems} styles={{ root: { padding: 0 } }} />
    </Stack>
  );
};
